/*
 * Copyright (c) 2021. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import _keys from "lodash/keys";
import _toString from "lodash/toString";
import _pickBy from "lodash/pickBy";
import _pick from "lodash/pick";
import _identity from "lodash/identity";
import _defaultTo from "lodash/defaultTo";
import _get from "lodash/get";
import _replace from "lodash/replace";
import _trimStart from "lodash/trimStart";
import moment, {Moment} from "moment";
import _forEach from "lodash/forEach";
import * as messagesActions from "../store/messages/actions";
import {checkFileFormat} from "./mediaObjects";
import {SERVICE_TIMES} from "./globalVars";
import _trim from "lodash/trim";
import _cloneDeep from "lodash/cloneDeep";
import _merge from "lodash/merge";
import _has from "lodash/has";
import _set from "lodash/set";
import {AbstractCase} from "../types/Entities/AbstractCase";
import {Customer} from "../types/Entities/Customer";
import {FormDataTypes} from "../types/FormDataTypes";
import Insurance from "../types/Entities/Insurance";
import {UseMutationResult} from "@tanstack/react-query";
import {CreateMediaObjectParams} from "./reactQuery/reactQueryMediaObjectService";
import {CreateResourceParams, DeleteResourceParams, UpdateResourceParams} from "./reactQuery/reactQueryService";
import {Person} from "../types/Entities/Person";
import {Address} from "../types/Entities/Address";
import {Organization} from "../types/Organization";
import {ConversionField} from "../types/FormServiceBase";

export const handleSimpleArray = (data: Record<string, string | object>) => {
  return _keys(_pickBy(data, _identity));
};

export const reduceSimpleArray = (data: string[]) => {
  return data.reduce<Record<string, boolean>>((object, arrayValue) => {
    object[arrayValue] = true;
    return object;
  }, {});
};

export const formValue = (formData: FormDataTypes["values"], path: string, defaultValue = "") => {
  return _defaultTo(_get(formData, path), defaultValue);
};

export const formObjectValue = (
  formData: FormDataTypes["values"],
  formDataPath: string,
  object: object,
  objectPath: string | null = null,
  defaultValue = "",
  condition = false
) => {
  if (condition) {
    return formValue(formData, formDataPath, defaultValue);
  }
  objectPath = objectPath || formDataPath;
  defaultValue = _get(object, objectPath) || defaultValue;
  return formValue(formData, formDataPath, defaultValue);
};

export const toUserInputtedFloat = (value: string | null) => {
  if (value === null) {
    return null;
  }
  return Number.parseFloat(_replace(_replace(value, ".", ""), ",", "."));
};

export const fromUserInputtedFloat = (value: string | null) => {
  if (value === null) {
    return null;
  }
  return _replace(value, ".", ",");
};

export const toFloat = (value: string | null) => {
  if (value === null) {
    return null;
  }
  return parseFloat(value);
};

export const fromFloat = (value: string | number | null) => {
  if (value === null) {
    return null;
  }
  // replace the dot with a comma, so it looks like a german number
  // the react-imask IMaskInput in ThousandsNumberInputFloat can handle both dots and commas as decimal separators
  return String(value).replace(".", ",");
};

export const toNumber = (value: string | null) => {
  if (value === null) {
    return null;
  }
  return Number.parseInt(value, 10);
};

export const fromNumber = (value: string | null) => {
  if (value === null) {
    return null;
  }
  return _toString(value);
};

export const toBoolean = (value: string | null) => {
  if (value === null) {
    return null;
  }
  return value === "yes";
};

export const fromBoolean = (value: boolean | null) => {
  if (value === null) {
    return null;
  }
  return value ? "yes" : "no";
};

export const fromGender = (value?: string | null) => {
  if (!value) {
    return value;
  }
  return value === "male" ? "Herr" : "Frau";
};

export const toIban = (value: string | null) => {
  if (!value) {
    return null;
  }
  return "DE" + _replace(value, /\s/g, "");
};

export const fromIban = (value: string | null) => {
  if (!value) {
    return null;
  }
  return _trimStart(value, "DE");
};

export const fromDate = (value: string) => {
  if (!value) {
    return null;
  }
  return convertToDate(value);
};

export const toDate = (value: Moment) => {
  if (value && value.isValid()) {
    return value.format();
  }
  return null;
};

//use this only if null should be a selectable option
export const toOpposingPartyType = (
  opposingPartyType: "opposingPartyPerson" | "opposingPartyLegalEntity" | "null"
): "opposingPartyPerson" | "opposingPartyLegalEntity" | null => {
  return opposingPartyType === "null" ? null : opposingPartyType;
};

//use this only if null should be a selectable option
export const fromOpposingPartyType = (
  opposingPartyType: "opposingPartyPerson" | "opposingPartyLegalEntity" | null
): "opposingPartyPerson" | "opposingPartyLegalEntity" | "null" => {
  return opposingPartyType ?? "null";
};

export const convertToDate = (value: string) => {
  return moment(value);
};

export const fromInsurance = (value: Insurance) => (!!value ? "/insurances/" + value.id : value);
export const toInsurance = (value: Insurance) => value;

export const fromOrganization = (value: Organization) => (!!value ? "/organizations/" + value.id : value);
export const toOrganization = (value: Organization) => value;

export const saveMediaObject = async (
  createMediaObjectMutation: UseMutationResult<any, unknown, CreateMediaObjectParams>,
  data: CreateMediaObjectParams["data"],
  errorMsg = null,
  successMsg = null
) => {
  try {
    const response = await createMediaObjectMutation.mutateAsync({ data: data });

    if (successMsg) {
      messagesActions.addMessage({ type: "success", text: successMsg });
    }
    return response;
  } catch (e) {
    messagesActions.addMessage({
      type: "error",
      text: errorMsg || "Es ist ein Fehler beim Upload Ihrer Datei aufgetreten. Bitte kontrollieren Sie die Datei.",
    });
    return false;
  }
};
export const persistAdditionalClients = async (
  formData: FormDataTypes["values"],
  product: AbstractCase,
  updateMutation: UseMutationResult<any, unknown, UpdateResourceParams>,
  createMutation: UseMutationResult<any, unknown, CreateResourceParams>,
  deleteMutation: UseMutationResult<any, unknown, DeleteResourceParams>
) => {
  if (product.additionalClients) {
    const idsOfNewAdditionalClients = formData.additionalClients.map((client: Person) => client.id);
    for (const additionalClient of product.additionalClients) {
      if (!idsOfNewAdditionalClients.includes(additionalClient.id)) {
        await deleteMutation.mutateAsync({
          uri: "/people",
          id: additionalClient.id,
        });
      }
    }
  }

  for (const additionalClient of formData.additionalClients) {
    if (additionalClient.id) {
      await updateMutation.mutateAsync({
        uri: "/people",
        data: {
          givenName: additionalClient.givenName,
          familyName: additionalClient.familyName,
          gender: additionalClient.gender,
        },
        id: additionalClient.id,
      });
    } else {
      await createMutation.mutateAsync({
        uri: "/people",
        data: {
          givenName: additionalClient.givenName,
          familyName: additionalClient.familyName,
          gender: additionalClient.gender,
          type: "additionalClient",
          customerSpecifiedCaseId: product.id,
        },
      });
    }
  }
};
export const fileCheck = (target: HTMLInputElement, allowAudioAndVideo = false) => {
  const { value, files } = target;

  let errorMsg = "";
  let hasError = false;

  if (!checkFileFormat(value, allowAudioAndVideo)) {
    hasError = true;
    const allowedFileTypesString = allowAudioAndVideo ? "JPG, PNG, PDF, MPEG, MP3, MP4 und OPUS." : "JPG, PNG und PDF.";
    errorMsg = "Ihre Datei hat leider das falsche Format. Erlaubte Dateiformate sind " + allowedFileTypesString;
  }

  if (!files) {
    hasError = true;
    errorMsg = "Es wurde keine Datei hochgeladen. Bitte wählen Sie eine Datei aus.";
  } else if (files[0].size > 52428800) {
    hasError = true;
    errorMsg = "Ihre Datei ist leider zu groß. Die maximale Dateigröße ist 50 MB.";
  } else if (files[0].size === 0) {
    hasError = true;
    errorMsg = "Ihre Datei ist leer. Bitte kontrollieren Sie die Dateigröße.";
  }

  return {
    hasError: hasError,
    errorMsg: errorMsg,
  };
};

export const serviceTimes = () => {
  let serviceTimesArray: { flexible: string } & { [key: number]: string } = {
    flexible: "Ich bin flexibel",
  };
  for (let i = SERVICE_TIMES.start; i <= SERVICE_TIMES.end; i++) {
    serviceTimesArray[i] = `${i} Uhr`;
  }
  return serviceTimesArray;
};

export function trimValueIfSet<T>(field: string, values: FormDataTypes["values"]): T {
  if (values[field]) {
    values[field] = _trim(values[field]);
  }
  // @ts-ignore todo remove this when the form values have the correct generic type instead of record string any
  return values;
}

export const sanitizeAddressData = (addressData: Partial<Address>) => {
  let sanitizedAddressData = _cloneDeep(addressData);
  sanitizedAddressData = trimValueIfSet("streetAddress", sanitizedAddressData);
  sanitizedAddressData = trimValueIfSet("postalCode", sanitizedAddressData);
  sanitizedAddressData = trimValueIfSet("addressLocality", sanitizedAddressData);
  sanitizedAddressData = trimValueIfSet("postOfficeBoxAddressLocality", sanitizedAddressData);
  sanitizedAddressData = trimValueIfSet("postOfficeBoxPostalCode", sanitizedAddressData);
  return sanitizedAddressData;
};

export const sanitizePersonData = (personData: Partial<Person>) => {
  let sanitizedPersonData = _cloneDeep(personData);
  sanitizedPersonData = trimValueIfSet("givenName", sanitizedPersonData);
  sanitizedPersonData = trimValueIfSet("familyName", sanitizedPersonData);
  return sanitizedPersonData;
};

export const sanitizeOrganizationData = (organizationData: Partial<Organization>) => {
  let sanitizedOrganizationData = _cloneDeep(organizationData);
  sanitizedOrganizationData = trimValueIfSet("name", sanitizedOrganizationData);
  sanitizedOrganizationData = trimValueIfSet("legalName", sanitizedOrganizationData);
  sanitizedOrganizationData = trimValueIfSet("email", sanitizedOrganizationData);
  sanitizedOrganizationData = trimValueIfSet("telephone", sanitizedOrganizationData);
  return sanitizedOrganizationData;
};

export const pickAndConvert = (object: FormDataTypes["values"], fields: FormDataTypes["values"]) => {
  let pickedData = _pick(object, fields);
  _forEach(pickedData, (value, key) => {
    if (value === "") {
      pickedData[key] = null;
    }
  });
  return pickedData;
};

export const nl2br = (str?: string | null, is_xhtml?: boolean) => {
  if (typeof str === "undefined" || str === null) {
    return "";
  }
  const breakTag = is_xhtml || typeof is_xhtml === "undefined" ? "<br />" : "<br>";
  return (str + "").replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, "$1" + breakTag + "$2");
};

export const prepareFormDataBase = (product: AbstractCase, customer: Customer, conversionFields: ConversionField[]) => {
  let resultData = _merge({}, product);
  resultData.customer = _merge({}, customer);
  _forEach(conversionFields, (conversionField) => {
    const { path, convertFrom } = conversionField;
    if (_has(resultData, path)) {
      _set(resultData, path, convertFrom(_get(resultData, path)));
    }
  });
  return resultData;
};

export const getConvertedFormDataBase = (data: FormDataTypes["values"], conversionFields: ConversionField[]) => {
  let resultData = _merge({}, data);
  _forEach(conversionFields, (conversionField) => {
    const { path, convertTo } = conversionField;
    if (_has(resultData, path)) {
      _set(resultData, path, convertTo(_get(resultData, path)));
    }
  });
  return resultData;
};
